import type { SimpleChanges } from '@angular/core';
import { Component, inject, input } from '@angular/core';

import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import type { User } from '@core/account-types';
import { trackByProp } from '@core/common-legacy';
import type { ApprovalStepWithDecisions } from '@core/requisitions-types';
import { AssignedFlowsActions } from '@recruitee/requisitions-data';

import { RequisitionsFlowStepComponent } from '../../../components/requisitions-flow-step/requisitions-flow-step.component';
import { ApprovalFlowFacade } from '../../../facades/single-requisition/approval-flow.facade';
import { EditApproversFacade } from '../../../facades/single-requisition/edit-approvers.facade';
import { FlowStepViewContext } from '../../../requisitions.types';

@Component({
  selector: 'rt-requisitions-flow-steps-list-container',
  template: `
    @for (step of steps(); track trackById($index, step)) {
      <rt-requisitions-flow-step
        [flowStep]="step"
        [users]="users()"
        [isEditable]="editableSteps[step.id!]"
        [canSendReminders]="!!canSendReminders"
        [context]="context()"
        (edit)="editApprovers(step)"
        (sendReminder)="sendReminder($event)"
        (removeApprover)="removeApprover($event, step)"
      />
    }
  `,
  styleUrls: ['./requisitions-flow-steps-list-container.component.less'],
  imports: [RequisitionsFlowStepComponent],
  providers: [EditApproversFacade],
})
export class RequisitionsFlowStepsListContainerComponent {
  private readonly store = inject(Store);
  private readonly editApproversFacade = inject(EditApproversFacade);
  private readonly approvalFlowFacade = inject(ApprovalFlowFacade, { optional: true });

  public readonly requisitionId = input<number>(undefined!);
  public readonly steps = input<ApprovalStepWithDecisions[]>([]);
  public readonly users = input<Record<number, User>>(undefined!);
  public readonly context = input<FlowStepViewContext>(FlowStepViewContext.Page);
  public readonly isReadOnly = input<boolean>(undefined!);

  public editableSteps: Record<number, boolean> = {};
  public canSendReminders: boolean | undefined;

  public trackById = trackByProp<{ id?: number }>({ prop: 'id' });

  public ngOnChanges(changes: SimpleChanges) {
    if (this.users() && this.context() === FlowStepViewContext.Page) {
      this.setEditableSteps();
      this.canSendReminders = this.approvalFlowFacade?.canSendReminders;
    }
  }

  public editApprovers(step: ApprovalStepWithDecisions) {
    this.editApproversFacade
      .selectApprovers$(step)
      ?.pipe(take(1))
      .subscribe(newApproverIds => {
        if (!newApproverIds || !newApproverIds.length) return;
        this.store.dispatch(
          AssignedFlowsActions.updateApprovers({
            payload: {
              requisitionId: this.requisitionId(),
              stepId: step.id!,
              approverIds: newApproverIds,
            },
          }),
        );
      });
  }

  public removeApprover(id: number, step: ApprovalStepWithDecisions) {
    this.store.dispatch(
      AssignedFlowsActions.deleteApprover({
        payload: {
          requisitionId: this.requisitionId(),
          stepId: step.id!,
          approverId: id,
        },
      }),
    );
  }

  public sendReminder(approverDecisionId: number) {
    this.store.dispatch(
      AssignedFlowsActions.sendReminder({
        payload: { requisitionId: this.requisitionId(), approverDecisionId },
      }),
    );
  }

  private setEditableSteps() {
    this.editableSteps = {};
    this.steps().forEach(step => {
      this.editableSteps[step.id!] =
        this.editApproversFacade.isStepEditable(step) && !this.isReadOnly();
    });
  }
}
